import { BranchInfo, BranchOffice } from '@/interfaces/branch';

export type NormalizedBranchInfo = BranchInfo &
  Required<
    Pick<
      BranchInfo,
      | 'address'
      | 'city'
      | 'metro'
      | 'description'
      | 'is_active'
      | 'location'
      | 'work_time'
      | 'services'
      | 'office'
    >
  > & {
    _updated: string;
    _created: string;
  };

const WEEK_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

type BranchWorkTime = NonNullable<BranchInfo['work_time']>;

const defaultWorkDay = () => ({
  worked: false,
  start: '',
  end: '',
  breaks: [] as { start: string; end: string }[],
});

const defaultServices = () => ({
  cctv: false,
  damaged_bills: false,
  parts: false,
  recount_room: false,
  transfer: false,
  verified: false,
});

const resolveOffice = (raw: Record<string, any>): BranchOffice => {
  const embedded =
    typeof raw.profile_id === 'object' ? raw.profile_id?.office : undefined;

  const sources = [raw.office, embedded, raw.profile?.office];
  const pick = (key: keyof BranchOffice) =>
    sources.find((source) => source?.[key] != null)?.[key];

  return {
    name: pick('name') ?? '',
    logo: pick('logo') ?? null,
    logo_url: pick('logo_url') ?? null,
  };
};

export const getBranchOffice = (
  branch?: { office?: BranchOffice } | null
): BranchOffice => branch?.office ?? {};

export const normalizeBranch = (
  raw: Record<string, any>
): NormalizedBranchInfo => {
  const workTime = WEEK_DAYS.reduce<BranchWorkTime>((acc, day) => {
    const dayData = raw.work_time?.[day] ?? {};
    acc[day] = {
      ...defaultWorkDay(),
      ...dayData,
      breaks: Array.isArray(dayData.breaks) ? dayData.breaks : [],
    };
    return acc;
  }, {} as BranchWorkTime);

  return {
    ...raw,
    _id: raw._id,
    branch_type: raw.branch_type,
    profile_id: raw.profile?._id ?? raw.profile_id,
    profile: raw.profile,
    phone_id: raw.phone_id,
    phone: raw.phone,
    address: raw.address ?? '',
    address_clarification: raw.address_clarification ?? '',
    city: raw.city,
    metro: raw.metro,
    district: raw.district,
    location: raw.location ?? { type: 'Point', coordinates: [0, 0] },
    work_time: workTime,
    services: { ...defaultServices(), ...raw.services },
    modifications: raw.modifications,
    description: raw.description ?? '',
    is_active: Boolean(raw.is_active),
    office: resolveOffice(raw),
    is_admin_created: Boolean(raw.is_admin_created),
    site_id: raw.site_id,
    status: raw.status,
    last_modified: raw.last_modified,
    auto_update: raw.auto_update,
    open_status: raw.open_status,
    review_rating: raw.review_rating,
    pinned_expired: raw.pinned_expired,
    painted_expired: raw.painted_expired,
    ocb: raw.ocb,
    _created: raw._created ?? '',
    _updated: raw._updated ?? raw.last_modified ?? '',
  } as NormalizedBranchInfo;
};
